import { useState, useRef, useEffect } from 'react';
import { UserProfile } from '@/types/fitness';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { LogIn, LogOut, Menu, X, Shield, ChevronDown, Camera, BarChart2, Bot, Zap, LifeBuoy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import logo from '@/assets/logo-insane-force.jpg';
import { ThemeToggle } from '@/components/ThemeToggle';

interface NavbarProps {
  activeUser: string;
  perfil: UserProfile;
  onNavigate: (section: string) => void;
  coachViewMode?: boolean;
}

const mainNavItems = [
  { label: 'Dashboard', id: 'dashboard' },
  { label: 'Historial', id: 'historial' },
  { label: 'Calorías', id: 'calorias' },
  { label: 'Rutina', id: 'rutina' },
  { label: 'Plan', id: 'plan' },
];

const moreNavItems = [
  { label: 'Progreso Visual', id: 'progreso visual', icon: Camera },
  { label: 'Evaluación', id: 'evaluacion', icon: BarChart2 },
  { label: 'Marco IA', id: 'marco', icon: Bot },
  { label: 'Suplementos', id: 'energia', icon: Zap },
];

const ENFOQUE_LABELS: Record<string, string> = {
  lose_fat: 'Perder Grasa',
  gain_muscle: 'Ganar Músculo',
  maintenance: 'Mantenimiento',
  performance: 'Mejorar Rendimiento',
  health: 'Mejorar Salud',
};

function formatEnfoque(enfoque: string): string {
  const key = enfoque.toLowerCase().replace(/\s+/g, '_');
  return ENFOQUE_LABELS[key] || enfoque.replace(/_/g, ' ');
}

export function Navbar({ activeUser, perfil, onNavigate, coachViewMode = false }: NavbarProps) {
  const { user, isCoachOrAdmin, isAdmin, isCoach, isSupport, canHandleSupport, loading } = useUserRole();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    setDropdownOpen(false);
    await supabase.auth.signOut();
  };

  const handleNav = (id: string) => {
    // Normalize: remove accents, lowercase
    const normalized = id.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, '-');
    
    const element = document.getElementById(normalized);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
    setMoreOpen(false);
  };

  return (
    <>
      <nav className={`fixed left-0 right-0 glass-dark h-14 md:h-14 z-40 flex items-center justify-between px-3 md:px-6 shadow-2xl ${coachViewMode ? 'top-10' : 'top-0'}`}>
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-accent/40 to-transparent"></div>
        
        {/* Logo */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <img src={logo} alt="Insane Force Team" className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover ring-1 ring-accent/20" />
          <span className="hidden md:block text-[10px] font-black uppercase tracking-[0.25em] text-primary-foreground/40">
            Insane Force Team
          </span>
        </div>
        
        {/* Desktop nav — main items */}
        <div className="hidden md:flex items-center gap-6">
          {mainNavItems.map((item) => (
            <button 
              key={item.id}
              onClick={() => handleNav(item.id)}
              className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground/60 hover:text-accent transition-colors duration-200 relative group whitespace-nowrap"
            >
              {item.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-accent transition-all duration-300 group-hover:w-full"></span>
            </button>
          ))}

          {/* Más + dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground/60 hover:text-accent transition-colors duration-200 relative group whitespace-nowrap"
            >
              Más
              <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
              <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-accent transition-all duration-300 group-hover:w-full"></span>
            </button>

            {moreOpen && (
              <div className="absolute left-0 top-full mt-2 w-52 glass-dark border border-accent/20 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                {moreNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNav(item.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-xs font-bold text-primary-foreground/70 hover:text-accent hover:bg-accent/10 transition-colors uppercase tracking-wider"
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* El botón "Reservar" del navbar se quitó porque ahora vive
              en el CoachHubCard del home con CTA prominente. Duplicarlo
              en el navbar era redundante. */}

          {/* Theme toggle — desktop */}
          <div className="hidden md:block">
            <ThemeToggle variant="navbar" />
          </div>

          {/* Profile dropdown — desktop */}
          {user && (
            <div className="relative hidden md:block" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 hover:border-accent/40 transition-all duration-200"
              >
                <div className="text-right">
                  <div className="text-xs font-bold text-accent uppercase tracking-wider leading-none">{activeUser}</div>
                  <div className="text-[10px] text-primary-foreground/50 uppercase tracking-widest leading-none mt-0.5">{formatEnfoque(perfil.enfoque)}</div>
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-primary-foreground/50 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 glass-dark border border-accent/20 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-4 py-3 border-b border-primary-foreground/10">
                    <div className="text-sm font-bold text-primary-foreground">{activeUser}</div>
                    {!coachViewMode && (
                      <div className="text-xs text-primary-foreground/50 mt-0.5 truncate">{authUser?.email}</div>
                    )}
                    {coachViewMode && (
                      <div className="text-xs text-orange-400 mt-0.5">Modo Vista — Solo lectura</div>
                    )}
                  </div>

                  {!loading && (isAdmin || isCoach || isSupport) && (
                    <div className="py-1 border-b border-primary-foreground/10">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => { setDropdownOpen(false); navigate('/admin'); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-amber-400 hover:bg-amber-500/10 transition-colors"
                          >
                            <Shield className="w-4 h-4" />
                            Panel Administrador
                          </button>
                          <button
                            onClick={() => { setDropdownOpen(false); navigate('/admin/analytics'); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-amber-400 hover:bg-amber-500/10 transition-colors"
                          >
                            <BarChart2 className="w-4 h-4" />
                            Analítica
                          </button>
                        </>
                      )}
                      {isCoach && (
                        <button
                          onClick={() => { setDropdownOpen(false); navigate('/coach'); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-accent hover:bg-accent/10 transition-colors"
                        >
                          <Shield className="w-4 h-4" />
                          Panel Coach
                        </button>
                      )}
                      {isSupport && (
                        <button
                          onClick={() => { setDropdownOpen(false); navigate('/admin/soporte'); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-purple-400 hover:bg-purple-500/10 transition-colors"
                        >
                          <LifeBuoy className="w-4 h-4" />
                          Panel de Soporte
                        </button>
                      )}
                    </div>
                  )}

                  <div className="py-1">
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-primary-foreground/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Login button — desktop */}
          {!user && (
            <button
              onClick={() => navigate('/auth')}
              className="hidden md:flex items-center gap-2 px-5 py-2 rounded-full bg-accent text-accent-foreground text-xs font-black uppercase tracking-wider hover:bg-accent/80 transition-all duration-200 shadow-lg shadow-accent/20 glow-accent-sm"
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>Entrar</span>
            </button>
          )}

          {/* Theme toggle — mobile */}
          <ThemeToggle variant="navbar" className="md:hidden" />

          {/* Mobile auth button */}
          <button
            onClick={user ? handleSignOut : () => navigate('/auth')}
            className={`md:hidden w-10 h-10 min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center transition-all duration-200 ${
              user
                ? 'bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground/70'
                : 'bg-accent text-accent-foreground shadow-lg shadow-accent/30 glow-accent-sm'
            }`}
          >
            {user ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-10 h-10 min-w-[44px] min-h-[44px] flex items-center justify-center text-primary-foreground"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="absolute top-14 right-0 w-64 glass-dark border-l border-accent/20 shadow-2xl animate-slide-in-right"
            onClick={e => e.stopPropagation()}
          >
            {/* User info mobile */}
            <div className="p-4 border-b border-primary-foreground/10">
              <div className="text-sm font-bold text-accent uppercase tracking-wider">{activeUser}</div>
              {!coachViewMode && (
                <div className="text-xs text-primary-foreground/50 mt-0.5 truncate">{authUser?.email}</div>
              )}
              {coachViewMode && (
                <div className="text-xs text-orange-400 mt-0.5">Modo Vista — Solo lectura</div>
              )}
              <div className="text-xs text-primary-foreground/40 uppercase tracking-widest mt-0.5">{formatEnfoque(perfil.enfoque)}</div>
            </div>

            {/* Main nav items */}
            <div className="py-2">
              {mainNavItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className="w-full text-left px-5 py-3 min-h-[44px] text-sm font-black uppercase tracking-widest text-primary-foreground hover:text-accent hover:bg-accent/5 transition-all duration-200"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* More items */}
            <div className="border-t border-primary-foreground/10 py-2">
              <div className="px-5 py-1 text-[10px] font-black uppercase tracking-widest text-primary-foreground/30">
                Más
              </div>
              {moreNavItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNav(item.id)}
                    className="w-full flex items-center gap-3 px-5 py-3 min-h-[44px] text-sm font-bold text-primary-foreground/70 hover:text-accent hover:bg-accent/5 transition-all duration-200"
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Centro de Soporte (Reservar entrenamiento se quitó —
                ahora vive en el CoachHubCard del home con CTA). */}
            {user && (
              <div className="border-t border-primary-foreground/10 py-2">
                <button
                  onClick={() => { navigate('/soporte'); setMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-5 py-3 min-h-[44px] text-sm font-bold text-primary-foreground/70 hover:text-accent hover:bg-accent/5 transition-colors"
                >
                  <LifeBuoy className="w-4 h-4" />
                  Centro de Soporte
                </button>
              </div>
            )}

            {/* Role-based panel links */}
            {!loading && user && (isAdmin || isCoach || isSupport) && (
              <div className="border-t border-primary-foreground/10 py-2">
                {isAdmin && (
                  <button
                    onClick={() => { navigate('/admin'); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-5 py-3 min-h-[44px] text-sm font-bold text-amber-400 hover:bg-amber-500/10 transition-colors"
                  >
                    <Shield className="w-4 h-4" />
                    Panel Administrador
                  </button>
                )}
                {isCoach && (
                  <button
                    onClick={() => { navigate('/coach'); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-5 py-3 min-h-[44px] text-sm font-bold text-accent hover:bg-accent/10 transition-colors"
                  >
                    <Shield className="w-4 h-4" />
                    Panel Coach
                  </button>
                )}
                {/* Panel de Soporte — para cualquier user con rol
                 *  support. Aunque sea admin, queremos ofrecer
                 *  entrada directa sin pasar por panel admin. */}
                {isSupport && (
                  <button
                    onClick={() => { navigate('/admin/soporte'); setMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-5 py-3 min-h-[44px] text-sm font-bold text-purple-400 hover:bg-purple-500/10 transition-colors"
                  >
                    <LifeBuoy className="w-4 h-4" />
                    Panel de Soporte
                  </button>
                )}
              </div>
            )}
            
            {/* Mobile sign out */}
            <div className="p-4 border-t border-primary-foreground/10">
              <button
                onClick={() => { handleSignOut(); setMenuOpen(false); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold uppercase tracking-wider text-sm transition-all min-h-[44px] bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground/70 hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-400"
              >
                <LogOut className="w-4 h-4" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
